import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function Reports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [activeTab, setActiveTab] = useState('my-reports'); // 'my-reports' | 'templates'

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/reports');
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      
      const data = await res.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      // Handle if data is an array or object with error
      if (Array.isArray(data)) {
        setReports(data);
      } else if (data.rows) {
        setReports(data.rows);
      } else {
        console.warn('Unexpected response format:', data);
        setReports([]);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      alert('Failed to load reports: ' + err.message);
      setReports([]); // Set empty array on error so page still loads
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete report "${name}"?`)) return;
    
    try {
      await apiFetch(`/api/reports/${id}`, {
        method: 'DELETE'
      });
      fetchReports();
    } catch (err) {
      console.error('Error deleting report:', err);
      alert('Failed to delete report');
    }
  };

  const handleDuplicate = async (id, name) => {
    try {
      await apiFetch(`/api/reports/${id}/duplicate`, {
        method: 'POST'
      });
      fetchReports();
    } catch (err) {
      console.error('Error duplicating report:', err);
      alert('Failed to duplicate report');
    }
  };

  const handleRun = (id) => {
    navigate(`/reports/${id}/view`);
  };

  // Separate templates from custom reports
  const templates = reports.filter(r => r.is_template);
  const customReports = reports.filter(r => !r.is_template);
  
  // Get current list based on active tab
  const currentList = activeTab === 'templates' ? templates : customReports;
  
  // Filter reports
  const filteredReports = currentList.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (report.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSource = filterSource === 'all' || report.data_source === filterSource;
    return matchesSearch && matchesSource;
  });

  // Get unique data sources for filter
  const dataSources = ['all', ...new Set(reports.map(r => r.data_source))];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDataSourceLabel = (source) => {
    const labels = {
      jobs: 'Jobs',
      quotes: 'Quotes',
      invoices: 'Invoices',
      customers: 'Customers',
      ppm_contracts: 'PPM Contracts',
      products: 'Products'
    };
    return labels[source] || source;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="Reports"
        subtitle={activeTab === 'templates' ? `${templates.length} templates available` : `${customReports.length} custom reports`}
        backTo="/reports-and-templates"
        backLabel="Reports & Templates"
        sticky
        actionButton={
          activeTab === 'my-reports' ? (
            <button
              onClick={() => navigate('/reports/new')}
              className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-blue-50 transition flex items-center space-x-2"
            >
              <span>➕</span>
              <span>New Report</span>
            </button>
          ) : null
        }
      />

      {/* Tabs */}
      <div className="bg-white border-b sticky top-[116px] z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex space-x-1 border-b">
            <button
              onClick={() => setActiveTab('my-reports')}
              className={`px-6 py-3 font-medium transition relative ${
                activeTab === 'my-reports'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              My Reports
              {customReports.length > 0 && (
                <span className="ml-2 bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-xs">
                  {customReports.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`px-6 py-3 font-medium transition relative ${
                activeTab === 'templates'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📋 Templates
              <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                {templates.length}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white border-b sticky top-[172px] z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex space-x-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Data Source Filter */}
            <div className="w-48">
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {dataSources.map(source => (
                  <option key={source} value={source}>
                    {source === 'all' ? 'All Sources' : getDataSourceLabel(source)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Loading reports...</p>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              {activeTab === 'templates' ? (
                <>
                  <p className="text-lg mb-2">📋 No templates available</p>
                  <p className="text-sm">Templates will appear here when created</p>
                </>
              ) : (
                <>
                  <p className="text-lg mb-2">📊 No custom reports yet</p>
                  <p className="text-sm">Create your first report or use a template to get started</p>
                  <button
                    onClick={() => navigate('/reports/new')}
                    className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Create Report
                  </button>
                  <button
                    onClick={() => setActiveTab('templates')}
                    className="mt-2 ml-2 bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
                  >
                    Browse Templates
                  </button>
                </>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Data Source</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Created By</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Last Run</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Schedules</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredReports.map((report, index) => (
                  <tr 
                    key={report.id}
                    className={`hover:bg-blue-50 transition ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{report.name}</div>
                        {report.description && (
                          <div className="text-sm text-gray-500 truncate max-w-md">{report.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getDataSourceLabel(report.data_source)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {report.created_by_name || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(report.last_run_at)}
                    </td>
                    <td className="px-6 py-4">
                      {report.active_schedules > 0 ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {report.active_schedules} active
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleRun(report.id)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          title="Run Report"
                        >
                          ▶️ Run
                        </button>
                        {report.is_template ? (
                          // Template actions - no edit/delete, only duplicate
                          <button
                            onClick={() => handleDuplicate(report.id, report.name)}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                            title="Use This Template"
                          >
                            📋 Use Template
                          </button>
                        ) : (
                          // Custom report actions - full CRUD
                          <>
                            <button
                              onClick={() => navigate(`/reports/${report.id}/edit`)}
                              className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                              title="Edit Report"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDuplicate(report.id, report.name)}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                              title="Duplicate Report"
                            >
                              📋
                            </button>
                            <button
                              onClick={() => handleDelete(report.id, report.name)}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                              title="Delete Report"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer Count */}
        {filteredReports.length > 0 && (
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredReports.length} of {reports.length} reports
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;
